import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../services/leads/leads-data.service';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ActionHelper } from '../../../helper/action.helper';
import { Observable, Subscription, of, forkJoin } from "rxjs";
import { ConnectService } from "../../../services/connect-two-component/connect.service";
import { UserProfileShareService } from "../../../services/shared-service/user-profile-share.service";
import { AddressHelper } from "../../../helper/address.helper";
import { RESOURCES_DATA_SERVICE, ResourcesDataService } from "../../../services/resources/resources-data.service";
import { DownloadHelper } from "../../../helper/download.helper";
import { MatProgressBar } from "@angular/material/progress-bar";
import { ActionIconHelper } from "../../../helper/action-icon.helper";
import { Select, Store } from "@ngxs/store";
import { LeadActionState } from "../../store/lead-action/lead-action.state";
import { LeadAction } from "../../../models/action.model";
import { LeadData, LeadsDataAction } from "../../../models/leads/leads.model";
import { CheckedLeadsState } from "../../store/checked-leads/checked-leads.state";
import {
  MarkCheckedLeadAsReadUnread,
  SetCheckedLeads
} from "../../store/checked-leads/checked-leads.actions";
import { HideLoader, ShowLoader } from "../../store/loader/loader.actions";
import { switchMap, tap, finalize, catchError, map } from 'rxjs/operators';
import { UserTokenService } from "../../../services/user-service/user-token.service";
import { LeadsQueryFilters, LeadsQueryRequestHelper } from "../../../helper/leads-query-request.helper";

@Component({
  selector: 'fi-files-management',
  templateUrl: './files-management.component.html',
  styleUrls: ['./files-management.component.scss'],
  standalone: false,
})
export class FilesManagementComponent implements OnInit, OnDestroy, AfterViewInit {
  subscriptions: Subscription[] = [];
  leadIds: number[] = [];
  actionHelper = ActionIconHelper;
  leadProcessAction: LeadAction;
  leadAssignEnquiryAction: LeadAction;
  leadTypeAction: LeadAction;
  leadNotInterestedAction: LeadAction;
  appointmentReminderAction: LeadAction;
  leadCreateAction: LeadAction;
  openAdditionalActions = false;
  @Select(LeadActionState.getActions) actions$: Observable<LeadAction[]>;
  @Select(CheckedLeadsState.getCheckedLeads) checkedLeads$: Observable<{leads: number[], checkAll: boolean}>;
  checkedLeads: number[] = [];
  loadedLeadDetails: {leadId: number, actions: LeadsDataAction[], read: boolean}[] = [];

  @ViewChild('isNotLoadedBar') isNotLoadedBar: MatProgressBar;
  // @ViewChild('isLoadedBar') isLoadedBar: MatProgressBar;
  // @ViewChild('isLoadedPercentage') isLoadedPercentage: ElementRef;

  @ViewChild('menu') menu: ElementRef;
  @ViewChild('additionalAction') additionalAction: ElementRef;

  private timout;
  leadNotInterested = true;
  leadProcess = true;
  leadAssignEnquiry = true;
  leadType = true;
  appointmentReminder = true;
  offerDelay = true;
  markAsReadEnabled = false;
  markAsUnReadEnabled = false;

  constructor(
    @Inject(LEADS_DATA_SERVICE) private leadDataService: LeadsDataService,
    private readonly sharedProfileService: UserProfileShareService,
    private connectService: ConnectService,
    private dialog: MatDialog,
    private router: Router,
    private store: Store,
    public downloadHelper: DownloadHelper,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    @Inject(RESOURCES_DATA_SERVICE) private resourcesDataService: ResourcesDataService,
    private userTokenService: UserTokenService,
  ) {
  }

  ngOnInit(): void {
    const subscription1 = this.actions$.subscribe(data => {
      if (data?.length) {
        for (let action of data) {
          if (action.Name === 'LEAD_PROCESS') {
            this.leadProcessAction = action;
          } else if (action.Name === 'LEAD_FORWARD') {
            this.leadAssignEnquiryAction = action;
          } else if (action.Name === 'LEAD_TYPE') {
            this.leadTypeAction = action;
          }else if (action.Name === 'LEAD_NOT_INTERESTED') {
            this.leadNotInterestedAction = action;
          } else if (action.Name === 'LEAD_REMINDER') {
            this.appointmentReminderAction = action;
          } else if (action.Name === 'LEAD_CREATE') {
            this.leadCreateAction = action;
          }
        }
      }
    });

    const subscription2 = this.checkedLeads$
      .pipe(
        switchMap(res => {
          const ids = Array.isArray(res?.leads) ? res.leads : [];
          const checkAllLeads = !!res?.checkAll;

          // Nothing selected → clear and refresh once
          if (!ids.length) {
            this.checkedLeads = [];
            this.loadedLeadDetails = [];

            this.refreshLoadedActionsAccessibility();
            return of([]); // nothing further to do
          }

          this.checkedLeads = ids;

          // Compute which IDs are missing from cache
          const cached = new Set(this.loadedLeadDetails.map(x => x.leadId));
          const missing = ids.filter(id => !cached.has(id));

          // If everything is cached → just refresh once (no loader)
          if (missing.length === 0) {
            this.refreshLoadedActionsAccessibility();
            return of([]); // no network calls
          }

          // If we will fetch, show loader when checkAllLeads
          if (checkAllLeads) {
            this.store.dispatch(new ShowLoader());
          }

          // Build batched requests (each result carries its id)
          const reqs = missing.map(id =>
            this.leadsDataService.getLeadById(id).pipe(
              map(resp => ({ id, resp })),
              catchError(() => of({ id, resp: null })) // keep the batch alive
            )
          );

          // Run all missing in parallel, then merge into cache once
          return forkJoin(reqs).pipe(
            tap(results => {
              results.forEach(({ id, resp }) => {
                if (!resp?.success || !resp?.data) return;
                const item = {
                  leadId: id,
                  actions: resp.data.Actions,
                  read: resp.data.Lead.Read,
                };
                const idx = this.loadedLeadDetails.findIndex(x => x.leadId === id);
                if (idx === -1) this.loadedLeadDetails.push(item);
                else this.loadedLeadDetails[idx] = item;
              });
            }),
            finalize(() => {
              // Hide loader only if we showed it
              if (checkAllLeads) {
                this.store.dispatch(new HideLoader());
              }
              // Single refresh after the whole batch completes
              this.refreshLoadedActionsAccessibility();
            })
          );
        })
      )
      .subscribe();

    const subscription3 = this.connectService.getLeadIds().subscribe(ids => {
      if (ids) {
        this.leadIds = ids;
      }
    });

    const subscription4 = this.router.events.subscribe(
      (event) => {
        if (event instanceof NavigationStart) {
          if (event.url !== '/') {
            this.checkedLeads = [];
            this.store.dispatch(new SetCheckedLeads(this.checkedLeads, false));
          }
        }
      });

    this.subscriptions.push(subscription1, subscription2, subscription3, subscription4);
  }

  @HostListener('document:click', ['$event'])
  onClick(ev: MouseEvent): void {
    if (ev.target instanceof HTMLElement) {
      if (
          (this.additionalAction?.nativeElement as HTMLElement)?.contains(ev.target) ||
          (this.menu?.nativeElement as HTMLElement)?.contains(ev.target)
      ) {
        return;
      }
    }

    setTimeout(() => {
      this.openAdditionalActions = false;
    }, 50);
  }

  get downloadIsInProgress(): boolean {
    return DownloadHelper.isInProgress;
  }

  ngAfterViewInit(): void {
    if (!DownloadHelper.isInProgress) {
      this.isNotLoadedBar?._elementRef?.nativeElement?.classList.add('hidden');
      //this.isLoadedBar._elementRef?.nativeElement?.classList.add('hidden');
      //this.isLoadedPercentage?.nativeElement?.classList.add('hidden');
    } else {
      this.isNotLoadedBar?._elementRef?.nativeElement?.classList.remove('hidden');
      //this.isLoadedBar._elementRef?.nativeElement?.classList.add('hidden');
      //this.isLoadedPercentage?.nativeElement?.classList.add('hidden');
    }

    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd && val.url !== '/') {
        if (!DownloadHelper.isInProgress) {
          this.isNotLoadedBar?._elementRef?.nativeElement?.classList.add('hidden');
          //this.isLoadedBar._elementRef?.nativeElement?.classList.add('hidden');
          //this.isLoadedPercentage?.nativeElement?.classList.add('hidden');
        }
      }
    });

    DownloadHelper.downloadStarted.subscribe(item => {
      this.isNotLoadedBar?._elementRef?.nativeElement?.classList.add('hidden');
      //this.isLoadedBar._elementRef?.nativeElement?.classList.remove('hidden');
      //this.isLoadedPercentage?.nativeElement?.classList.remove('hidden');
      DownloadHelper.isInProgress = false;

      this.timout = setTimeout(() => {
        //this.isLoadedBar._elementRef?.nativeElement?.classList.add('hidden');
        //this.isLoadedPercentage?.nativeElement?.classList.add('hidden');
        this.timout = undefined;
        clearInterval(DownloadHelper.interval);
      }, 10000)
    })
  }

  newRequest(): void {
    ActionHelper.openNewRequestDialog(this.dialog, this.router);
    this.getProvinces();
  }

  markAsRead(): void {
    this.store.dispatch(new MarkCheckedLeadAsReadUnread(null));

    if (!this.checkedLeads?.length) {
      this.recomputeReadFlags();
      return;
    }

    const ops = this.checkedLeads.map(leadId =>
      this.leadDataService.markAsRead(leadId).pipe(
        map(({ success }) => ({ leadId, success })),
        catchError(() => of({ leadId, success: false }))
      )
    );

    forkJoin(ops).subscribe(results => {
      results.forEach(({ leadId, success }) => {
        if (!success) return;

        const lead = this.loadedLeadDetails.find(l => l.leadId == leadId);
        if (lead) {
          lead.read = true;
        }
        this.store.dispatch(new MarkCheckedLeadAsReadUnread(leadId));
      });

      this.recomputeReadFlags();
    });
  }

  markAsUnRead(): void {
    this.store.dispatch(new MarkCheckedLeadAsReadUnread(null));

    if (!this.checkedLeads?.length) {
      this.recomputeReadFlags();
      return;
    }

    const ops = this.checkedLeads.map(leadId =>
      this.leadDataService.markAsUnread(leadId).pipe(
        map(({ success }) => ({ leadId, success })),
        catchError(() => of({ leadId, success: false }))
      )
    );

    forkJoin(ops).subscribe(results => {
      results.forEach(({ leadId, success }) => {
        if (!success) {
          return;
        }

        const lead = this.loadedLeadDetails.find(l => l.leadId == leadId);
        if (lead) {
          lead.read = false;
        }
        this.store.dispatch(new MarkCheckedLeadAsReadUnread(leadId));
      });

      this.recomputeReadFlags();
    });
  }

  getProvinces(): void {
    this.resourcesDataService.getResourceProvinces().subscribe(({ success, data }) => {
      if (success) {
        AddressHelper.setProvinces(data);
      }
    });
  };

  openCreateRequestFromComposerDialog(): void {
    ActionHelper.showAction('CREATE_REQUEST_FROM_COMPOSER', this.dialog, {});
  }

  downloadRequest(): void {
    if (this.timout) {
      return;
    }
    this.isNotLoadedBar?._elementRef?.nativeElement?.classList?.remove('hidden');
    DownloadHelper.isInProgress = true;
    this.timout = true;

    if (this.checkedLeads?.length) {
      DownloadHelper.downloadSearchedLeadByIds(this.leadDataService, this.checkedLeads);
      return;
    }

    const filters = this.loadFilterSettings();
    const queryRequest = LeadsQueryRequestHelper.build(
      filters,
      +this.userTokenService.getId(),
      undefined,
      false
    );

    DownloadHelper.downloadQueryLeads(this.leadDataService, queryRequest);
  }

  loadFilterSettings(): LeadsQueryFilters {
    let filters: LeadsQueryFilters = {};

    try {
      filters = JSON.parse(localStorage.getItem('filters') ?? '{}') ?? {};
    } catch (e) {
      console.log(e);
    }

    return {
      ...filters,
      closedLeads: filters.closedLeads ?? false,
      onlyMyLeads: filters.onlyMyLeads ?? true,
      unreadLeads: filters.unreadLeads ?? false,
      openLeads: filters.openLeads ?? true,
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub?.unsubscribe());
    this.checkedLeads = [];
  }

  async doAction(action: LeadsDataAction): Promise<void> {
    if (!this.checkedLeads.length) {
      return;
    }
    const result = ActionHelper.doAction({} as any, action, this.dialog, this.checkedLeads, true);

    result?.afterClosed().subscribe();
  }

  private recomputeReadFlags(): void {
    const leads = this.loadedLeadDetails;
    const hasLeads = leads.length > 0;
    // keep your original logic; change to `.some(...)` if that's what you actually want
    this.markAsReadEnabled = hasLeads && leads.every(l => !l.read);
    this.markAsUnReadEnabled = hasLeads && leads.every(l =>  l.read);
  }


  private refreshLoadedActionsAccessibility(): void {
    // Keep only the checked leads (avoid mutating while iterating)
    this.loadedLeadDetails = this.loadedLeadDetails.filter(
      lead => this.checkedLeads.includes(lead.leadId)
    );

    const leads = this.loadedLeadDetails;
    const hasLeads = leads.length > 0;

    // Helpers
    const leadHasAction = (lead: any, name: string) =>
      (lead.actions ?? []).some((a: any) => a?.Name === name);

    const everyHasAction = (name: string) =>
      hasLeads && leads.every(l => leadHasAction(l, name));

    this.recomputeReadFlags();

    // Actions are available only if every selected lead supports them
    this.leadNotInterested   = everyHasAction('LEAD_NOT_INTERESTED');
    this.leadProcess         = everyHasAction('LEAD_PROCESS');
    this.leadAssignEnquiry   = everyHasAction('LEAD_FORWARD');
    this.leadType            = everyHasAction('LEAD_TYPE');
    this.appointmentReminder = everyHasAction('LEAD_REMINDER');
    this.offerDelay          = everyHasAction('OFFER_DELAY');
  }

}
